import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { useToast } from '@/contexts/ToastContext';
import { useChatStream } from '@/hooks/useChatStream';
import { getSocket } from '@/hooks/useRealtimeSync';
import { API_CHAT_URL } from '@/config/serverConfig';
import {
  api,
  ApiError,
  type AgentChatListItem,
  type MessengerMessage,
} from '@/api/client';
import { createPortal } from 'react-dom';
import {
  AddIcon,
  PostAddIcon,
  SendIcon,
  CloseIcon,
  ImageIcon,
  BuildIcon,
  CalendarMonthIcon,
  ChevronRightIcon,
} from '@/components/ui/Icons';
import { AttachmentCard } from '@/components/ui/AttachmentCard/AttachmentCard';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { useClickOutside } from '@/hooks/useClickOutside';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { AutoExpandScrollTextarea } from '@/components/ui/AutoExpandScrollTextarea/AutoExpandScrollTextarea';
import '@/components/ui/ContextMenu/ContextMenu.css';
import { useTeleportOptional } from '@/contexts/TeleportContext';
import { generateUUID } from '@/utils/generateUUID';
import './AgentChatView.css';
import { ChatCanvas } from '@/components/Messenger/canvas/ChatCanvas';
import { AgentProcessingStatusRow } from '@/components/Messenger/shared/status/AgentProcessingStatusRow';
import { MessengerChatHeader } from '@/components/Messenger/shared/chrome/MessengerChatHeader';
import { MessengerMessagesLoader } from '@/components/Messenger/shared/chrome/MessengerMessagesLoader';
import { MessengerMutedStatus } from '@/components/Messenger/shared/chrome/MessengerMutedStatus';
import { MessengerComposerLayout } from '@/components/Messenger/shared/composer/MessengerComposerLayout';
import { MessageStrip } from '@/components/Messenger/shared/composer/MessageStrip';
import { MessengerComposerContextAttachBadges } from '@/components/Messenger/shared/composer/MessengerComposerContextAttachBadges';
import { MessengerComposerImagePreviews } from '@/components/Messenger/shared/composer/MessengerComposerImagePreviews';
import {
  AGENT_CHAT_FILE_ACCEPT,
  isAgentChatAttachableFile,
} from '@/components/Messenger/shared/attachments/agentChatUploadAccept';
import {
  mergeChatContextAttachOneSlot,
  readChatAttachFromDataTransfer,
} from '@/components/Messenger/shared/composer/readChatAttachFromDataTransfer';
import { AGENT_EXCLUSIVE_ATTACHMENTS_HINT } from '@/components/Messenger/shared/composer/messengerAttachmentLimits';
import {
  CHAT_PART_ATTACHED_PLANNER,
  CHAT_PART_ATTACHED_PROJECT,
  CHAT_PART_ATTACHED_SESSION,
  type ChatAttachedProjectPayload,
  type ChatAttachedSessionPayload,
  type TrackerChatMessage,
} from '@/components/Messenger/types/chatMessageTypes';
import {
  AgentModelControls,
  DEFAULT_MODELS,
  nextModel,
  type ChatModelId,
} from '@/components/Messenger/agent/AgentModelControls';
import { AgentMessageList } from '@/components/Messenger/agent/AgentMessageList';
import { AGENT_ASSISTANT_FORWARD_LABEL } from '@/components/Messenger/agent/agentAssistantForwardDraft';
import {
  isSlotQuotaBlocked,
  tryExtractQuotaFromChatTransportError,
  type AgentChatModelOption,
  type AgentChatQuotaSnapshot,
} from '@/components/Messenger/agent/agentChatSharedTypes';
import {
  MESSENGER_AGENT_CHAT_RECOVER,
  type MessengerAgentChatRecoverDetail,
} from '@/constants/messengerEvents';
import { useAgentChatThread } from '@/components/Messenger/agent/useAgentChatThread';
import {
  getMessengerDraft,
  removeMessengerDraftFromStore,
  removeMessengerDraftFromStoreAtRevision,
  subscribeMessengerDraft,
  upsertMessengerDraftInStore,
} from '@/components/Messenger/cache/messengerDraftsStore';
import {
  MessengerDraftSaveScheduler,
  shouldApplyRemoteDraftToComposer,
} from '@/components/Messenger/shared/messengerComposerDraftSync';
import {
  createAgentComposerDraftSnapshot,
  createPendingAgentDraftSend,
  extractAgentComposerTextFromParts,
} from '@/components/Messenger/agent/agentComposerDraftState';

type AttachedProject = ChatAttachedProjectPayload;
type AttachedSession = ChatAttachedSessionPayload;

interface AgentChatViewProps {
  /** Ключ экземпляра `useChat` в панели (вкладка в памяти ChatShell; не путать с id чата в БД). */
  uiSessionId: string;
  /** id сохранённого чата в БД или null до первого сообщения. */
  serverChatId: string | null;
  /** После `POST /api/agent-chats` — встроить строку списка в ChatShell без лишнего refetch. */
  onServerChatCreated: (row: AgentChatListItem) => void;
  /** Чат в БД недоступен (удалён, другая среда): сбросить привязку к серверу в оболочке. */
  onServerChatAccessLost?: () => void;
  /** Обновить список чатов в сайдбаре (после первого сообщения и по желанию). */
  onRefreshAgentChatsList?: () => void;
  /** Синхронизировать «идёт обработка» в ChatShell для мгновенного спиннера в сайдбаре/вкладке. */
  onProcessingStateChange?: (chatId: string, processing: boolean) => void;
  /** Состояние обработки текущего serverChatId из ChatShell (живёт независимо от useChat-статуса). */
  processingExternal?: boolean;
  onNewChat: () => void;
  onClose?: () => void;
  /** Пересылка ответа ассистента в личку (через модалку в ChatShell). */
  onForwardMessage: (draft: MessengerMessage) => void;
  /** false, когда открыт другой чат — не перехватываем фокус в скрытой панели. */
  paneActive?: boolean;
  /** См. ChatView — вместе с paneActive для догона низа при открытии попапа. */
  messengerSurfaceVisible?: boolean;
  /** Заголовок текущего сохранённого чата из списка сайдбара (в т.ч. по сокету после авто-имени). */
  persistedListTitle?: string | null;
  /** Последний слот модели чата из БД (agent_chats.last_model_slot) для восстановления после reload. */
  persistedListModelSlot?: ChatModelId | null;
}

const RESPONSE_TIMEOUT_MS = 120000;

const AGENT_QUOTA_BLOCKED_TOAST =
  'Лимит генераций на сегодня исчерпан для выбранной модели. Переключите слот или дождитесь сброса в 00:00 (МСК).';

function formatAgentChatTransportError(raw: string): string {
  const t = raw.trim();
  if (t.startsWith('{') && t.includes('"error"')) {
    try {
      const j = JSON.parse(t) as { error?: unknown };
      if (typeof j.error === 'string' && j.error.trim()) return j.error.trim();
    } catch {
      /* не JSON */
    }
  }
  return raw;
}

function normalizeForSend(text: string): string {
  return text.trim().replace(/(\r?\n){2,}/g, '\n\n');
}

function agentDraftSnapshotSignature(text: string): string {
  return JSON.stringify(createAgentComposerDraftSnapshot(text));
}

function mergePartsReplaceText(
  baseline: TrackerChatMessage['parts'],
  text: string
): TrackerChatMessage['parts'] {
  const nonText = (baseline ?? []).filter((p) => {
    if (!p || typeof p !== 'object') return true;
    return (p as { type?: string }).type !== 'text';
  }) as TrackerChatMessage['parts'];
  return [...nonText, { type: 'text', text }] as TrackerChatMessage['parts'];
}


function buildEditStripMessage(
  anchorId: string,
  parts: TrackerChatMessage['parts']
): MessengerMessage {
  let fileUrl: string | null = null;
  let fileName: string | null = null;
  let mimeType: string | null = null;
  for (const p of parts ?? []) {
    if (!p || typeof p !== 'object') continue;
    const o = p as {
      type?: string;
      url?: unknown;
      filename?: unknown;
      mediaType?: unknown;
    };
    if (o.type !== 'file') continue;
    if (typeof o.url === 'string' && o.url.trim()) fileUrl = o.url.trim();
    if (typeof o.filename === 'string' && o.filename.trim()) fileName = o.filename.trim();
    if (typeof o.mediaType === 'string' && o.mediaType.trim()) mimeType = o.mediaType.trim();
    break;
  }
  return {
    id: anchorId,
    thread_id: 'agent-chat',
    sender_id: 'me',
    body: extractAgentComposerTextFromParts(parts),
    file_url: fileUrl,
    file_name: fileName,
    mime_type: mimeType,
    created_at: new Date().toISOString(),
  };
}

function buildOutgoingAgentUserMessage(
  id: string,
  parts: TrackerChatMessage['parts']
): TrackerChatMessage {
  return {
    id,
    role: 'user',
    parts,
    metadata: {
      messageCreatedAt: new Date().toISOString(),
    },
  } as TrackerChatMessage;
}

export function AgentChatView({
  uiSessionId,
  serverChatId,
  onServerChatCreated,
  onServerChatAccessLost,
  onRefreshAgentChatsList,
  onProcessingStateChange,
  processingExternal = false,
  onNewChat,
  onClose,
  onForwardMessage,
  paneActive = true,
  messengerSurfaceVisible = true,
  persistedListTitle = null,
  persistedListModelSlot = null,
}: AgentChatViewProps) {
  const { showError: showToastError } = useToast();
  const { state: liveStream, reset: resetLiveStream } = useChatStream(serverChatId);
  const teleport = useTeleportOptional();
  const [chatModels, setChatModels] = useState<AgentChatModelOption[]>(DEFAULT_MODELS);
  const [chatModelId, setChatModelId] = useState<ChatModelId>('premium');
  const [agentQuota, setAgentQuota] = useState<AgentChatQuotaSnapshot | null>(null);

  /** Квота на пользователя; при смене сохранённого чата или вкладки (новый черновик) досинхронизируем с сервером. */
  useEffect(() => {
    let cancelled = false;
    void api.chat
      .quota()
      .then((q) => {
        if (!cancelled) setAgentQuota(q);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [serverChatId, uiSessionId]);

  useEffect(() => {
    if (!agentQuota?.reset_at) return;
    const end = Date.parse(agentQuota.reset_at);
    if (Number.isNaN(end)) return;
    const ms = end - Date.now() + 1500;
    if (ms <= 0 || ms > 48 * 3600 * 1000) return;
    const id = window.setTimeout(() => {
      void api.chat
        .quota()
        .then(setAgentQuota)
        .catch(() => {});
    }, ms);
    return () => window.clearTimeout(id);
  }, [agentQuota?.reset_at, agentQuota?.day_msk]);

  useEffect(() => {
    let cancelled = false;
    void api.chat
      .models()
      .then((rows) => {
        if (cancelled) return;
        const mapped = rows
          .filter(
            (r): r is AgentChatModelOption =>
              r.id === 'premium' || r.id === 'premium_pro'
          )
          .map((r) => ({
            id: r.id,
            label: r.label,
            can_stop: Boolean(r.can_stop),
          }));
        if (mapped.length > 0) setChatModels(mapped);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const chatModelRef = useRef(chatModelId);
  chatModelRef.current = chatModelId;

  useEffect(() => {
    if (!serverChatId) return;
    if (persistedListModelSlot !== 'premium' && persistedListModelSlot !== 'premium_pro') {
      return;
    }
    const nextModel: ChatModelId = persistedListModelSlot;
    setChatModelId((prev) => (prev === nextModel ? prev : nextModel));
  }, [serverChatId, persistedListModelSlot]);
  const pendingModelOneShotRef = useRef<ChatModelId | null>(null);
  const paneActiveRef = useRef(paneActive);
  paneActiveRef.current = paneActive;

  const [input, setInput] = useState('');
  const [files, setFiles] = useState<FileList | undefined>(undefined);
  const [attachedProjects, setAttachedProjects] = useState<AttachedProject[]>([]);
  const [attachedSessions, setAttachedSessions] = useState<AttachedSession[]>([]);
  const [plannerActive, setPlannerActive] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [attachSubmenuEl, setAttachSubmenuEl] = useState<HTMLElement | null>(null);
  const attachSubmenuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const plannerDataRef = useRef<string | null>(null);
  const attachedProjectsRef = useRef<AttachedProject[]>([]);
  const attachedSessionsRef = useRef<AttachedSession[]>([]);
  const attachMenuRef = useRef<HTMLButtonElement>(null);
  const attachPopoverRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastAgentChatToastError = useRef<string | null>(null);
  const serverChatIdRef = useRef<string | null>(serverChatId);
  const pendingAgentAttachmentIdsRef = useRef<string[]>([]);
  const activeStreamChatIdRef = useRef<string | null>(serverChatId);
  const skipHistoryLoadOnceRef = useRef(false);
  const preEditDraftRef = useRef<{
    input: string;
    files: FileList | undefined;
    plannerActive: boolean;
    attachedProjects: AttachedProject[];
    attachedSessions: AttachedSession[];
  } | null>(null);
  const lastRetryPayloadRef = useRef<{ parts: TrackerChatMessage['parts'] } | null>(null);
  const [pendingEdit, setPendingEdit] = useState<{
    anchorId: string;
    baselineParts: TrackerChatMessage['parts'];
  } | null>(null);
  const [hydratedDraftChatId, setHydratedDraftChatId] = useState<string | null>(null);
  const draftTargetAgentChatIdRef = useRef<string | null>(serverChatId);
  const draftHydrationGenerationRef = useRef(0);
  const isComposerFocusedRef = useRef(false);
  const localDraftRevisionMsRef = useRef(0);
  const lastSyncedDraftSignatureRef = useRef<string | null>(null);
  const pendingAgentDraftSendRef = useRef<{ chatId: string; text: string } | null>(null);

  const draftSchedulerRef = useRef<MessengerDraftSaveScheduler | null>(null);
  if (draftSchedulerRef.current === null) {
    draftSchedulerRef.current = new MessengerDraftSaveScheduler({
      save: async (snapshot, clientUpdatedAt) => {
        const targetId = draftTargetAgentChatIdRef.current;
        if (!targetId) return;
        const result = await api.messenger.putDraft('agent_chat', targetId, {
          ...snapshot,
          client_updated_at: clientUpdatedAt,
        });
        if (result.draft) {
          upsertMessengerDraftInStore(result.draft);
        } else {
          removeMessengerDraftFromStoreAtRevision('agent_chat', targetId, clientUpdatedAt);
        }
      },
      clear: async (clientUpdatedAt) => {
        const targetId = draftTargetAgentChatIdRef.current;
        if (!targetId) return;
        removeMessengerDraftFromStoreAtRevision('agent_chat', targetId, clientUpdatedAt);
        const result = await api.messenger.deleteDraft('agent_chat', targetId, clientUpdatedAt);
        if (result.draft) {
          upsertMessengerDraftInStore(result.draft);
        }
      },
    });
  }
  const draftScheduler = draftSchedulerRef.current;

  const clearPersistedAgentDraft = useCallback(
    async (chatId: string) => {
      if (draftTargetAgentChatIdRef.current === chatId) {
        lastSyncedDraftSignatureRef.current = agentDraftSnapshotSignature('');
        localDraftRevisionMsRef.current = Date.now();
        await draftScheduler.discard();
      }
      const clientUpdatedAt = new Date().toISOString();
      removeMessengerDraftFromStoreAtRevision('agent_chat', chatId, clientUpdatedAt);
      try {
        const result = await api.messenger.deleteDraft('agent_chat', chatId, clientUpdatedAt);
        if (result.draft) upsertMessengerDraftInStore(result.draft);
      } catch {
        // Sending succeeded; draft cleanup remains best effort and reconciles on reconnect.
      }
    },
    [draftScheduler]
  );

  useEffect(() => {
    serverChatIdRef.current = serverChatId;
  }, [serverChatId]);

  const {
    shouldRender: attachMenuShouldRender,
    contentClasses: attachMenuContentClasses,
    requestClose: attachMenuRequestClose,
  } = useOverlayAnimation(attachMenuOpen, () => setAttachMenuOpen(false), true);

  usePopoverPositioning({
    triggerRef: attachMenuRef,
    popoverRef: attachPopoverRef,
    isOpen: attachMenuShouldRender,
    preferredPositionY: 'top',
    priorityAxis: 'y',
    horizontalAlign: 'center',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1100,
  });

  useClickOutside(attachPopoverRef, attachMenuRequestClose, {
    enabled: attachMenuShouldRender,
    capture: false,
    excludeRefs: [attachMenuRef],
    excludeSelector: '.context-menu',
  });

  const cancelAttachSubmenuClose = useCallback(() => {
    if (attachSubmenuTimerRef.current) {
      clearTimeout(attachSubmenuTimerRef.current);
      attachSubmenuTimerRef.current = null;
    }
  }, []);

  const scheduleAttachSubmenuClose = useCallback(() => {
    attachSubmenuTimerRef.current = setTimeout(() => {
      setAttachSubmenuEl(null);
      attachSubmenuTimerRef.current = null;
    }, 150);
  }, []);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const addAttachableFile = useCallback((newFiles: File[]) => {
    const file = newFiles.find((f) => isAgentChatAttachableFile(f));
    if (!file) return;
    setAttachedProjects([]);
    setAttachedSessions([]);
    setPlannerActive(false);
    const dt = new DataTransfer();
    dt.items.add(file);
    setFiles(dt.files);
  }, []);

  // DnD drop handler — business logic only; ChatView owns isDraggingOver + overlay
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (e.dataTransfer.files.length > 0) {
        setAttachedProjects([]);
        setAttachedSessions([]);
        setPlannerActive(false);
        addAttachableFile(Array.from(e.dataTransfer.files));
        return;
      }
      const incoming = readChatAttachFromDataTransfer(e.dataTransfer);
      if (incoming.projects.length === 0 && incoming.sessions.length === 0) return;
      setFiles(undefined);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setPlannerActive(false);
      const next = mergeChatContextAttachOneSlot(
        attachedProjectsRef.current,
        attachedSessionsRef.current,
        incoming
      );
      setAttachedProjects(next.projects);
      setAttachedSessions(next.sessions);
    },
    [addAttachableFile]
  );

  const removeFile = useCallback(
    (index: number) => {
      if (!files) return;
      const dt = new DataTransfer();
      Array.from(files).forEach((f, i) => {
        if (i !== index) dt.items.add(f);
      });
      setFiles(dt.files.length > 0 ? dt.files : undefined);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [files]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        addAttachableFile(imageFiles.slice(0, 1));
      }
    },
    [addAttachableFile]
  );

  attachedProjectsRef.current = attachedProjects;
  attachedSessionsRef.current = attachedSessions;

  useEffect(() => {
    const generation = ++draftHydrationGenerationRef.current;
    setHydratedDraftChatId(null);

    void (async () => {
      await draftScheduler.flush();
      if (generation !== draftHydrationGenerationRef.current) return;

      draftTargetAgentChatIdRef.current = serverChatId;
      if (preEditDraftRef.current !== null) return;

      setInput('');
      setFiles(undefined);
      setPlannerActive(false);
      setAttachedProjects([]);
      setAttachedSessions([]);
      if (fileInputRef.current) fileInputRef.current.value = '';

      if (!serverChatId) {
        lastSyncedDraftSignatureRef.current = agentDraftSnapshotSignature('');
        localDraftRevisionMsRef.current = 0;
        return;
      }

      const draft = getMessengerDraft('agent_chat', serverChatId);
      lastSyncedDraftSignatureRef.current = agentDraftSnapshotSignature(draft?.text ?? '');
      localDraftRevisionMsRef.current = draft ? Date.parse(draft.updated_at) || 0 : 0;
      setInput(draft?.text ?? '');
      setHydratedDraftChatId(serverChatId);
    })();

    return () => {
      draftHydrationGenerationRef.current += 1;
    };
  }, [draftScheduler, serverChatId]);

  useEffect(() => {
    if (
      !serverChatId ||
      hydratedDraftChatId !== serverChatId ||
      preEditDraftRef.current !== null ||
      pendingEdit !== null ||
      pendingAgentDraftSendRef.current !== null
    ) {
      return;
    }

    const snapshot = createAgentComposerDraftSnapshot(input);
    const signature = agentDraftSnapshotSignature(input);
    if (signature === lastSyncedDraftSignatureRef.current) return;

    const clientUpdatedAt = draftScheduler.schedule(snapshot);
    lastSyncedDraftSignatureRef.current = signature;
    localDraftRevisionMsRef.current = Date.parse(clientUpdatedAt);
    if (!input.trim()) {
      removeMessengerDraftFromStore('agent_chat', serverChatId);
      return;
    }
    upsertMessengerDraftInStore(
      {
        target_kind: 'agent_chat',
        target_id: serverChatId,
        ...snapshot,
        updated_at: clientUpdatedAt,
      },
      { source: 'local' }
    );
  }, [draftScheduler, hydratedDraftChatId, input, pendingEdit, serverChatId]);

  const applyStoredAgentDraftIfAllowed = useCallback(() => {
    if (!serverChatId || hydratedDraftChatId !== serverChatId) return;
    const draft = getMessengerDraft('agent_chat', serverChatId);
    const remoteUpdatedAtMs = draft ? Date.parse(draft.updated_at) || 0 : Date.now();
    if (
      !shouldApplyRemoteDraftToComposer({
        isComposerFocused: isComposerFocusedRef.current,
        isEditingMessage: preEditDraftRef.current !== null,
        localRevisionMs: localDraftRevisionMsRef.current,
        remoteUpdatedAtMs,
      })
    ) {
      return;
    }

    lastSyncedDraftSignatureRef.current = agentDraftSnapshotSignature(draft?.text ?? '');
    localDraftRevisionMsRef.current = remoteUpdatedAtMs;
    setInput(draft?.text ?? '');
  }, [hydratedDraftChatId, serverChatId]);

  useEffect(() => {
    if (!serverChatId) return;
    return subscribeMessengerDraft('agent_chat', serverChatId, applyStoredAgentDraftIfAllowed);
  }, [applyStoredAgentDraftIfAllowed, serverChatId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') void draftScheduler.flush();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [draftScheduler]);

  useEffect(() => {
    return () => draftScheduler.dispose();
  }, [draftScheduler]);

  const handleComposerFocus = useCallback(() => {
    isComposerFocusedRef.current = true;
  }, []);

  const handleComposerBlur = useCallback(() => {
    isComposerFocusedRef.current = false;
    applyStoredAgentDraftIfAllowed();
    void draftScheduler.flush();
  }, [applyStoredAgentDraftIfAllowed, draftScheduler]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: API_CHAT_URL,
        credentials: 'include',
        body: () => {
          const pendingSlot = pendingModelOneShotRef.current;
          if (pendingSlot !== null) pendingModelOneShotRef.current = null;
          const resolvedSlot = (pendingSlot ?? chatModelRef.current) as ChatModelId;
          const b: Record<string, unknown> = {
            model: resolvedSlot as string,
          };
          const sid = serverChatIdRef.current;
          if (sid) {
            b.agentChatId = sid;
            const aids = [...pendingAgentAttachmentIdsRef.current];
            pendingAgentAttachmentIdsRef.current = [];
            if (aids.length > 0) {
              b.agentAttachmentIds = aids;
            }
          }
          const pd = plannerDataRef.current;
          if (pd != null && pd !== '') {
            b.plannerData = pd;
            plannerDataRef.current = null;
          }
          const ap = attachedProjectsRef.current;
          if (ap?.length) {
            b.attachedProjects = ap;
            attachedProjectsRef.current = [];
          }
          const as = attachedSessionsRef.current;
          if (as?.length) {
            b.attachedSessions = as;
            attachedSessionsRef.current = [];
          }
          return b;
        },
      }),
    []
  );

  const { messages, sendMessage, status, stop, error, clearError, setMessages } = useChat<TrackerChatMessage>({
    id: uiSessionId,
    experimental_throttle: 120,
    transport,
    onFinish: ({ message, isAbort, isError }) => {
      const pendingDraft = pendingAgentDraftSendRef.current;
      if (isAbort || isError) {
        pendingAgentDraftSendRef.current = null;
        if (pendingDraft) setInput(pendingDraft.text);
        return;
      }
      if (message.role !== 'assistant') return;
      pendingAgentDraftSendRef.current = null;
      if (pendingDraft) {
        void clearPersistedAgentDraft(pendingDraft.chatId);
      }
      if (serverChatIdRef.current) {
        onRefreshAgentChatsList?.();
      }
      if (paneActiveRef.current) {
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    },
  });

  useEffect(() => {
    const onRecover = (e: Event) => {
      const detail = (e as CustomEvent<MessengerAgentChatRecoverDetail>).detail;
      const chatId = detail?.chatId?.trim() || '';
      if (!chatId || chatId !== serverChatIdRef.current) return;
      if (detail.processing) return;
      if (status !== 'streaming' && status !== 'submitted') return;
      stop();
    };
    window.addEventListener(MESSENGER_AGENT_CHAT_RECOVER, onRecover);
    return () => {
      window.removeEventListener(MESSENGER_AGENT_CHAT_RECOVER, onRecover);
    };
  }, [status, stop]);

  const {
    historicalMessages,
    historyLoading,
    messagesRef,
    refreshPersistedChatMessages,
    replaceLocalThread,
  } = useAgentChatThread({
    serverChatId,
    uiSessionId,
    paneActive,
    messengerSurfaceVisible,
    liveStreamActive: liveStream.isActive,
    messages,
    setMessages,
    status,
    skipHistoryLoadOnceRef,
    lastRetryPayloadRef,
    activeStreamChatIdRef,
    onProcessingStateChange,
    onServerChatAccessLost,
    onRefreshAgentChatsList,
    showError: showToastError,
    resetLiveStream,
  });

  useEffect(() => {
    setPendingEdit(null);
    preEditDraftRef.current = null;
  }, [uiSessionId]);

  useEffect(() => {
    lastAgentChatToastError.current = null;
  }, [uiSessionId]);

  const markChatProcessing = useCallback(
    (chatId: string | null | undefined, processing: boolean) => {
      const normalized = typeof chatId === 'string' ? chatId.trim() : '';
      if (!normalized) return;
      onProcessingStateChange?.(normalized, processing);
    },
    [onProcessingStateChange]
  );

  useEffect(() => {
    const socket = getSocket();
    const onAgentChatQuota = (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      setAgentQuota(raw as AgentChatQuotaSnapshot);
    };
    socket.on('messenger:agent_chat_quota', onAgentChatQuota);
    return () => {
      socket.off('messenger:agent_chat_quota', onAgentChatQuota);
    };
  }, []);

  useEffect(() => {
    if (!error) {
      lastAgentChatToastError.current = null;
      return;
    }
    const pendingDraft = pendingAgentDraftSendRef.current;
    pendingAgentDraftSendRef.current = null;
    if (pendingDraft) setInput(pendingDraft.text);
    const fromBody = tryExtractQuotaFromChatTransportError(error.message);
    if (fromBody) setAgentQuota(fromBody);
    else void api.chat.quota().then(setAgentQuota).catch(() => {});
    const msg = formatAgentChatTransportError(error.message);
    if (msg === 'Чат недоступен') {
      onServerChatAccessLost?.();
    }
    if (lastRetryPayloadRef.current) {
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const last = prev[prev.length - 1];
        if (last?.role !== 'user') return prev;
        if (last.metadata?.sendFailed) return prev;
        return [
          ...prev.slice(0, -1),
          { ...last, metadata: { ...last.metadata, sendFailed: true } },
        ];
      });
    }
    if (lastAgentChatToastError.current === msg) {
      clearError();
      return;
    }
    lastAgentChatToastError.current = msg;
    showToastError(msg);
    clearError();
  }, [error, clearError, showToastError, setMessages, onServerChatAccessLost]);

  const isStreaming = status === 'streaming' || status === 'submitted';
  const isStreamingCurrentChat =
    isStreaming &&
    !!activeStreamChatIdRef.current &&
    activeStreamChatIdRef.current === serverChatIdRef.current;
  const streamBusy = processingExternal || isStreamingCurrentChat || liveStream.isActive;

  const selectedModelBlocked = useMemo(
    () => isSlotQuotaBlocked(agentQuota, chatModelId),
    [agentQuota, chatModelId]
  );

  const canStopCurrent = useMemo(() => {
    const m = chatModels.find((x) => x.id === chatModelId);
    return Boolean(m?.can_stop);
  }, [chatModels, chatModelId]);

  const canStopRef = useRef(canStopCurrent);
  canStopRef.current = canStopCurrent;

  const showStreamStopButton = streamBusy && canStopCurrent;

  const composerInputDisabled = useMemo(
    () =>
      status !== 'ready' ||
      historyLoading ||
      streamBusy ||
      (!pendingEdit && selectedModelBlocked),
    [status, historyLoading, streamBusy, pendingEdit, selectedModelBlocked]
  );

  /** Над полем — только лимит генераций; остальные блокировки без текстовой плашки. */
  const composerDisabledNotice = useMemo((): string | null => {
    if (!pendingEdit && selectedModelBlocked) {
      return 'Лимит генераций на сегодня для выбранной модели исчерпан. Смените модель или дождитесь сброса в 00:00 (МСК).';
    }
    return null;
  }, [pendingEdit, selectedModelBlocked]);

  const streamPhase = useMemo((): string | null => {
    if (!streamBusy) return null;
    if (liveStream.phase === 'tool') {
      for (let i = liveStream.toolCalls.length - 1; i >= 0; i--) {
        if (liveStream.toolCalls[i].status === 'calling') {
          return `tool:${liveStream.toolCalls[i].toolName}`;
        }
      }
      return 'tool';
    }
    if (liveStream.phase) return liveStream.phase;
    if (processingExternal) {
      return 'submitted';
    }
    return 'thinking';
  }, [streamBusy, liveStream, processingExternal]);

  const statusText = useMemo((): string => {
    if (!streamPhase) return '';
    if (streamPhase === 'reasoning' || streamPhase === 'thinking' || streamPhase === 'submitted') {
      return 'Думаю';
    }
    if (streamPhase === 'tool' || streamPhase.startsWith('tool:')) return 'Вызываю инструменты';
    if (streamPhase === 'text') return 'Пишу ответ';
    return 'Думаю';
  }, [streamPhase]);

  useEffect(() => {
    if (status === 'streaming' || status === 'submitted') {
      timeoutRef.current = setTimeout(() => {
        if (canStopRef.current) stop();
      }, RESPONSE_TIMEOUT_MS);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [status, stop]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = normalizeForSend(input);

    if (pendingEdit) {
      if (!text || status !== 'ready' || historyLoading || streamBusy) {
        if (!text) showToastError('Введите текст');
        return;
      }
      if (isSlotQuotaBlocked(agentQuota, chatModelId)) {
        showToastError(AGENT_QUOTA_BLOCKED_TOAST);
        return;
      }
      try {
        const mergedParts = mergePartsReplaceText(pendingEdit.baselineParts, text);
        const anchorId = pendingEdit.anchorId;
        const ok = await rollbackToUserMessage(anchorId);
        if (!ok) return;
        const composerDraft = preEditDraftRef.current;
        setPendingEdit(null);
        preEditDraftRef.current = null;
        lastRetryPayloadRef.current = { parts: mergedParts };
        let reuseId: string | null = null;
        setMessages((m) => {
          if (m.length === 0) return m;
          const last = m[m.length - 1];
          if (last?.role === 'user' && last.id === anchorId) {
            reuseId = last.id;
            return m.slice(0, -1);
          }
          return m;
        });
        const uid = reuseId ?? anchorId;
        pendingAgentAttachmentIdsRef.current = [];
        activeStreamChatIdRef.current = serverChatIdRef.current;
        markChatProcessing(activeStreamChatIdRef.current, true);
        sendMessage(buildOutgoingAgentUserMessage(uid, mergedParts));
        setInput(composerDraft?.input ?? '');
        setFiles(composerDraft?.files);
        setPlannerActive(composerDraft?.plannerActive ?? false);
        setAttachedProjects(composerDraft?.attachedProjects ?? []);
        setAttachedSessions(composerDraft?.attachedSessions ?? []);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err) {
        showToastError((err as Error).message || 'Ошибка отправки');
      }
      return;
    }

    if (
      (!text &&
        !files?.length &&
        !plannerActive &&
        !attachedProjects.length &&
        !attachedSessions.length) ||
      status !== 'ready' ||
      historyLoading ||
      streamBusy
    )
      return;
    if (selectedModelBlocked) {
      showToastError(AGENT_QUOTA_BLOCKED_TOAST);
      return;
    }
    const nFile = files?.length ? 1 : 0;
    const nCtx = attachedProjects.length > 0 || attachedSessions.length > 0 ? 1 : 0;
    const nPl = plannerActive ? 1 : 0;
    if (nFile + nCtx + nPl > 1) {
      showToastError(AGENT_EXCLUSIVE_ATTACHMENTS_HINT);
      return;
    }
    const userAttachedPlanner = plannerActive;
    if (userAttachedPlanner) {
      try {
        const { text: plannerText } = await api.planner.getSummary();
        plannerDataRef.current = plannerText;
      } catch {
        plannerDataRef.current = null;
      }
    }
    try {
      let sid = serverChatIdRef.current;
      if (!sid) {
        const row = await api.agentChats.create();
        sid = row.id;
        serverChatIdRef.current = sid;
        draftTargetAgentChatIdRef.current = sid;
        skipHistoryLoadOnceRef.current = true;
        onServerChatCreated(row);
      }
      const attachmentIds: string[] = [];
      const file0 = files?.[0];
      let fileParts: Array<Record<string, unknown>> = [];
      if (file0) {
        const up = await api.agentChats.upload(sid, file0);
        attachmentIds.push(up.attachment_id);
        fileParts = [
          {
            type: 'file',
            url: up.url,
            mediaType: up.mime_type ?? file0.type,
            filename: up.file_name ?? file0.name,
            attachmentId: up.attachment_id,
          },
        ];
      }
      const projectParts = attachedProjects.map((p) => ({
        type: CHAT_PART_ATTACHED_PROJECT,
        data: { id: p.id, name: p.name || p.id },
      }));
      const sessionParts = attachedSessions.map((s) => ({
        type: CHAT_PART_ATTACHED_SESSION,
        data: {
          id: s.id,
          date: s.date,
          project_id: s.project_id,
          label: s.label || s.date,
        },
      }));
      const plannerParts = userAttachedPlanner
        ? ([{ type: CHAT_PART_ATTACHED_PLANNER, data: {} }] as const)
        : [];
      const allParts = [
        ...projectParts,
        ...sessionParts,
        ...plannerParts,
        ...fileParts,
        { type: 'text', text: text || '' },
      ] as TrackerChatMessage['parts'];
      pendingAgentAttachmentIdsRef.current = attachmentIds;
      lastRetryPayloadRef.current = { parts: allParts };
      const uid = generateUUID();
      activeStreamChatIdRef.current = sid;
      markChatProcessing(sid, true);
      pendingAgentDraftSendRef.current = createPendingAgentDraftSend(sid, input);
      sendMessage(buildOutgoingAgentUserMessage(uid, allParts));
      setInput('');
      setFiles(undefined);
      setPlannerActive(false);
      setAttachedProjects([]);
      setAttachedSessions([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const pendingDraft = pendingAgentDraftSendRef.current;
      pendingAgentDraftSendRef.current = null;
      if (pendingDraft) setInput(pendingDraft.text);
      showToastError((err as Error).message || 'Ошибка отправки');
    }
  };

  const handleRetrySend = useCallback(() => {
    const payload = lastRetryPayloadRef.current;
    if (!payload || status !== 'ready' || streamBusy) return;
    if (isSlotQuotaBlocked(agentQuota, chatModelId)) {
      showToastError(AGENT_QUOTA_BLOCKED_TOAST);
      return;
    }
    let reuseId: string | null = null;
    setMessages((m) => {
      if (m.length === 0) return m;
      const last = m[m.length - 1];
      if (last?.role === 'user') {
        reuseId = last.id;
        return m.slice(0, -1);
      }
      return m;
    });
    const uid = reuseId ?? generateUUID();
    pendingAgentAttachmentIdsRef.current = [];
    activeStreamChatIdRef.current = serverChatIdRef.current;
    markChatProcessing(activeStreamChatIdRef.current, true);
    pendingAgentDraftSendRef.current = createPendingAgentDraftSend(
      serverChatIdRef.current,
      extractAgentComposerTextFromParts(payload.parts),
    );
    sendMessage(buildOutgoingAgentUserMessage(uid, payload.parts));
  }, [sendMessage, setMessages, status, streamBusy, markChatProcessing, agentQuota, chatModelId, showToastError]);

  const rollbackToUserMessage = useCallback(
    async (anchorId: string): Promise<boolean> => {
      const prev = messagesRef.current;
      const idx = prev.findIndex((m) => m.id === anchorId);
      if (idx < 0 || prev[idx]?.role !== 'user') return false;
      const anchorParts = (prev[idx].parts ?? []) as TrackerChatMessage['parts'];
      const truncated = prev.slice(0, idx + 1);
      const sid = serverChatIdRef.current;
      if (sid) {
        try {
          await api.agentChats.truncateAfter(sid, anchorId);
        } catch (e: unknown) {
          const msg =
            e instanceof ApiError && typeof e.message === 'string'
              ? e.message
              : 'Не удалось отрезать историю';
          showToastError(msg);
          return false;
        }
        /** Не подставляем `truncated` из messagesRef в кэш: у стриминговых ассистентов нет modelSlot, перезапись ломала подпись слота до reload. */
        await refreshPersistedChatMessages(sid);
      } else {
        replaceLocalThread(truncated);
      }
      lastRetryPayloadRef.current = { parts: anchorParts };
      return true;
    },
    [showToastError, refreshPersistedChatMessages, replaceLocalThread, messagesRef]
  );

  const handleEditFromMenu = useCallback(
    (anchorId: string) => {
      if (status !== 'ready' || historyLoading || streamBusy) return;
      const current = messagesRef.current;
      const idx = current.findIndex((m) => m.id === anchorId);
      if (idx < 0 || current[idx]?.role !== 'user') return;
      const baseline = (current[idx].parts ?? []) as TrackerChatMessage['parts'];
      preEditDraftRef.current = {
        input,
        files,
        plannerActive,
        attachedProjects,
        attachedSessions,
      };
      setPendingEdit({ anchorId, baselineParts: baseline });
      setInput(extractAgentComposerTextFromParts(baseline));
      setFiles(undefined);
      setPlannerActive(false);
      setAttachedProjects([]);
      setAttachedSessions([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    [
      status,
      historyLoading,
      streamBusy,
      input,
      files,
      plannerActive,
      attachedProjects,
      attachedSessions,
      messagesRef,
    ]
  );

  const cancelPendingEdit = useCallback(() => {
    const draft = preEditDraftRef.current;
    preEditDraftRef.current = null;
    setPendingEdit(null);
    if (draft) {
      setInput(draft.input);
      setFiles(draft.files);
      setPlannerActive(draft.plannerActive);
      setAttachedProjects(draft.attachedProjects);
      setAttachedSessions(draft.attachedSessions);
    } else {
      setInput('');
      setFiles(undefined);
      setPlannerActive(false);
      setAttachedProjects([]);
      setAttachedSessions([]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  useEffect(() => {
    if (!pendingEdit) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      cancelPendingEdit();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pendingEdit, cancelPendingEdit]);

  const handleRetryWithModel = useCallback(
    async (anchorId: string, modelId: ChatModelId) => {
      if (status !== 'ready' || historyLoading || streamBusy) return;
      if (isSlotQuotaBlocked(agentQuota, modelId)) {
        showToastError(AGENT_QUOTA_BLOCKED_TOAST);
        return;
      }
      const ok = await rollbackToUserMessage(anchorId);
      if (!ok) return;
      pendingModelOneShotRef.current = modelId;
      setChatModelId((prev) => (prev === modelId ? prev : modelId));
      const payload = lastRetryPayloadRef.current;
      if (!payload) return;
      let reuseId: string | null = null;
      setMessages((m) => {
        if (m.length === 0) return m;
        const last = m[m.length - 1];
        if (last?.role === 'user') {
          reuseId = last.id;
          return m.slice(0, -1);
        }
        return m;
      });
      const uid = reuseId ?? anchorId;
      pendingAgentAttachmentIdsRef.current = [];
      activeStreamChatIdRef.current = serverChatIdRef.current;
      markChatProcessing(activeStreamChatIdRef.current, true);
      pendingAgentDraftSendRef.current = createPendingAgentDraftSend(
        serverChatIdRef.current,
        extractAgentComposerTextFromParts(payload.parts),
      );
      sendMessage(buildOutgoingAgentUserMessage(uid, payload.parts));
    },
    [
      rollbackToUserMessage,
      sendMessage,
      setMessages,
      status,
      historyLoading,
      streamBusy,
      markChatProcessing,
      agentQuota,
      showToastError,
    ]
  );

  const removeAttachedProject = useCallback((projectId: string) => {
    setAttachedProjects((prev) => prev.filter((p) => p.id !== projectId));
  }, []);

  const removeAttachedSession = useCallback((sessionId: string) => {
    setAttachedSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  useEffect(() => {
    if (status === 'ready' && liveStream.isDone) {
      const timer = setTimeout(() => resetLiveStream(), 200);
      return () => clearTimeout(timer);
    }
  }, [status, liveStream.isDone, resetLiveStream]);

  const handlePresetClick = (text: string) => {
    if (status !== 'ready' || historyLoading || streamBusy) return;
    if (selectedModelBlocked) {
      showToastError(AGENT_QUOTA_BLOCKED_TOAST);
      return;
    }
    setInput(text);
  };

  const PRESET_MESSAGES = [
    'Дай сводку по проекту',
    'Давай добавим чтецов в базу',
    'Давай запланируем сессию',
  ];

  const EMPTY_GREETINGS = [
    'Я готова помочь решить любой вопрос внутри трекера. Что тебя интересует?',
    'Чем могу быть полезна? Задай любой вопрос о проектах, сессиях или чтецах.',
    'Готова помочь с трекером. Что тебя интересует?',
    'Я здесь, чтобы помочь. Какой вопрос по трекеру тебя волнует?',
  ];

  const emptyGreeting = useMemo(
    () => EMPTY_GREETINGS[Math.floor(Math.random() * EMPTY_GREETINGS.length)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleModelCycle = useCallback(() => {
    setChatModelId((prev) => nextModel(prev));
  }, []);

  const isModelSendBlocked = useCallback(
    (id: ChatModelId) => isSlotQuotaBlocked(agentQuota, id),
    [agentQuota]
  );

  const sendQuickMessage = useCallback(
    (m: { text: string }) => {
      if (isSlotQuotaBlocked(agentQuota, chatModelId)) {
        showToastError(AGENT_QUOTA_BLOCKED_TOAST);
        return;
      }
      activeStreamChatIdRef.current = serverChatIdRef.current;
      markChatProcessing(activeStreamChatIdRef.current, true);
      sendMessage(m as Parameters<typeof sendMessage>[0]);
    },
    [sendMessage, markChatProcessing, agentQuota, chatModelId, showToastError]
  );

  const agentMessageListEl = useMemo(
    () => (
      <AgentMessageList
        animationContextId={`agent:${uiSessionId}`}
        historicalMessages={historicalMessages}
        status={status}
        teleport={teleport}
        historyLoading={historyLoading}
        streamBusy={streamBusy}
        agentQuota={agentQuota}
        chatModelId={chatModelId}
        chatModels={chatModels}
        onForwardMessage={onForwardMessage}
        sendQuickMessage={sendQuickMessage}
        onEditMessage={handleEditFromMenu}
        onRetryUserWithModel={handleRetryWithModel}
        onRetrySend={handleRetrySend}
        isModelSendBlocked={isModelSendBlocked}
      />
    ),
    [
      uiSessionId,
      historicalMessages,
      status,
      teleport,
      historyLoading,
      streamBusy,
      agentQuota,
      chatModelId,
      chatModels,
      onForwardMessage,
      sendQuickMessage,
      handleEditFromMenu,
      handleRetryWithModel,
      handleRetrySend,
      isModelSendBlocked,
    ]
  );

  const composerPreview =
    Boolean(files?.length) ||
    plannerActive ||
    attachedProjects.length > 0 ||
    attachedSessions.length > 0 ? (
      <div className="chatAssistant__attachZone">
        <MessengerComposerContextAttachBadges
          attachedProjects={attachedProjects}
          attachedSessions={attachedSessions}
          onRemoveProject={removeAttachedProject}
          onRemoveSession={removeAttachedSession}
          teleport={teleport}
        />
        <MessengerComposerImagePreviews files={files} onRemove={removeFile} />
        {plannerActive ? (
          <AttachmentCard
            variant="planner"
            title="Планер"
            subtitle="Контекст"
            onRemove={() => setPlannerActive(false)}
            removeButtonClassName="chatAssistant__close"
          />
        ) : null}
      </div>
    ) : undefined;

  const leadingAttachSlot = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={AGENT_CHAT_FILE_ACCEPT}
        className="chatAssistant__fileInput"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f || !isAgentChatAttachableFile(f)) {
            setFiles(undefined);
            return;
          }
          setAttachedProjects([]);
          setAttachedSessions([]);
          setPlannerActive(false);
          const dt = new DataTransfer();
          dt.items.add(f);
          setFiles(dt.files);
        }}
        aria-label="Прикрепить фото или Excel"
      />
      <button
        ref={attachMenuRef}
        type="button"
        className="chatAssistant__attachBtn"
        onClick={() => setAttachMenuOpen((o) => !o)}
        disabled={status !== 'ready' || historyLoading || streamBusy || pendingEdit != null}
        aria-label="Добавить"
        aria-expanded={attachMenuOpen}
      >
        <AddIcon size={20} />
      </button>
      {attachMenuShouldRender &&
        createPortal(
          <div ref={attachPopoverRef} style={{ position: 'fixed', left: '-9999px', top: '-9999px' }}>
            <OverlayIntrinsicSize>
              <div className={`context-menu ${attachMenuContentClasses}`} role="menu">
            <ul className="context-menu__list">
              <li className="context-menu__list-item">
                <button
                  type="button"
                  className="context-menu__item"
                  role="menuitem"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    attachMenuRequestClose();
                    fileInputRef.current?.click();
                  }}
                >
                  <span className="context-menu__iconCell" aria-hidden>
                    <ImageIcon size={18} />
                  </span>
                  <span className="context-menu__item-text">Фото или Excel</span>
                </button>
              </li>
              <li
                className="context-menu__list-item"
                onMouseEnter={(e) => {
                  cancelAttachSubmenuClose();
                  setAttachSubmenuEl(e.currentTarget);
                }}
                onMouseLeave={scheduleAttachSubmenuClose}
              >
                <button type="button" className="context-menu__action context-menu__action_with-sub">
                  <BuildIcon size={18} />
                  <span className="context-menu__action-text">Инструменты</span>
                  <ChevronRightIcon size={16} className="context-menu__action-chevron" />
                </button>
                {attachSubmenuEl && (
                  <ContextMenuSubmenu
                    anchorEl={attachSubmenuEl}
                    onMouseEnter={cancelAttachSubmenuClose}
                    onMouseLeave={() => setAttachSubmenuEl(null)}
                  >
                    <ul className="context-menu__list">
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          role="menuitem"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            attachMenuRequestClose();
                            setAttachSubmenuEl(null);
                            setFiles(undefined);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                            setAttachedProjects([]);
                            setAttachedSessions([]);
                            setPlannerActive(true);
                          }}
                        >
                          <CalendarMonthIcon size={18} />
                          <span className="context-menu__action-text">Планер</span>
                        </button>
                      </li>
                    </ul>
                  </ContextMenuSubmenu>
                )}
              </li>
            </ul>
              </div>
            </OverlayIntrinsicSize>
          </div>,
          document.body
        )}
    </>
  );

  const header = useMemo(
    () => (
      <MessengerChatHeader
        title={persistedListTitle?.trim() || AGENT_ASSISTANT_FORWARD_LABEL}
        right={
          <>
            <AgentModelControls
              chatModels={chatModels}
              chatModelId={chatModelId}
              onCycleModel={handleModelCycle}
              disabled={streamBusy || historyLoading}
              showLoader={streamBusy || historyLoading}
              quota={agentQuota}
            />
            <button
              type="button"
              onClick={onNewChat}
              className="chatAssistant__newChatBtn"
              title="Новый чат"
            >
              <PostAddIcon size={20} />
            </button>
            {onClose && (
              <button
                type="button"
                className="chatAssistant__close"
                onClick={onClose}
                aria-label="Закрыть"
              >
                <CloseIcon size={20} />
              </button>
            )}
          </>
        }
      />
    ),
    [
      persistedListTitle,
      chatModels,
      chatModelId,
      handleModelCycle,
      streamBusy,
      historyLoading,
      onNewChat,
      onClose,
      agentQuota,
    ]
  );

  const editStrip = pendingEdit ? (
    <MessageStrip
      variant="edit"
      message={buildEditStripMessage(pendingEdit.anchorId, pendingEdit.baselineParts)}
      senderLabel="Вы"
      onCancel={cancelPendingEdit}
    />
  ) : undefined;

  const composer = (
    <>
      <MessengerComposerLayout
        onSubmit={handleSubmit}
        previewZone={composerPreview}
        replyStrip={editStrip}
        noticeSlot={
          composerDisabledNotice ? (
            <MessengerMutedStatus>{composerDisabledNotice}</MessengerMutedStatus>
          ) : undefined
        }
        leadingSlot={leadingAttachSlot}
        textarea={
          <AutoExpandScrollTextarea
            ref={inputRef}
            className="chatAssistant__input form__field_inline"
            inputClassName="chatAssistant__inputField"
            placeholder={pendingEdit ? 'Редактировать сообщение' : 'Введите сообщение'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={handleComposerFocus}
            onBlur={handleComposerBlur}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && pendingEdit) {
                e.preventDefault();
                cancelPendingEdit();
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                const text = normalizeForSend(input);
                const canSendEdit =
                  Boolean(pendingEdit) &&
                  Boolean(text) &&
                  status === 'ready' &&
                  !historyLoading &&
                  !streamBusy &&
                  !selectedModelBlocked;
                const canSendNormal =
                  (text ||
                    files?.length ||
                    plannerActive ||
                    attachedProjects.length ||
                    attachedSessions.length) &&
                  status === 'ready' &&
                  !historyLoading &&
                  !streamBusy &&
                  !selectedModelBlocked;
                if (canSendEdit || canSendNormal) {
                  e.preventDefault();
                  void handleSubmit(e as unknown as React.FormEvent);
                }
              }
            }}
            disabled={composerInputDisabled}
            maxVisibleLines={22}
            fullWidth
          />
        }
        trailingSlot={
          showStreamStopButton ? (
            <button type="button" className="chatAssistant__stopBtn" onClick={stop} title="Остановить">
              <i className="fa-solid fa-stop" aria-hidden />
            </button>
          ) : streamBusy ? null : (
            <button
              type="submit"
              className="chatAssistant__sendBtn"
              disabled={
                pendingEdit
                  ? !normalizeForSend(input).trim() ||
                    composerInputDisabled ||
                    selectedModelBlocked
                  : (!input.trim() &&
                      !files?.length &&
                      !plannerActive &&
                      !attachedProjects.length &&
                      !attachedSessions.length) || composerInputDisabled
              }
              title={pendingEdit ? 'Сохранить' : 'Отправить'}
            >
              <SendIcon size={20} />
            </button>
          )
        }
      />
    </>
  );

  return (
    <ChatCanvas
      header={header}
      composer={composer}
      tailMessageId={historicalMessages[historicalMessages.length - 1]?.id}
      messageListLength={historicalMessages.length}
      messageFeedReady={!serverChatId || !historyLoading}
      contextId={uiSessionId}
      streamCue={streamBusy}
      messengerSurfaceVisible={messengerSurfaceVisible && paneActive}
      acceptDrop
      onDrop={handleDrop}
    >
      {serverChatId && historyLoading ? (
        <MessengerMessagesLoader label="Загрузка сообщений" />
      ) : (
        <>
          {historicalMessages.length === 0 && !streamBusy && (
            <div className="chatAssistant__empty">
              <p className="chatAssistant__emptyText">{emptyGreeting}</p>
              {!input.trim() &&
                !files?.length &&
                !attachedProjects.length &&
                !attachedSessions.length && (
                  <div className="chatAssistant__presets">
                    {PRESET_MESSAGES.map((msg) => (
                      <button
                        key={msg}
                        type="button"
                        className="chatAssistant__presetBtn"
                        disabled={
                          status !== 'ready' || historyLoading || streamBusy || selectedModelBlocked
                        }
                        onClick={() => handlePresetClick(msg)}
                      >
                        {msg}
                      </button>
                    ))}
                  </div>
                )}
            </div>
          )}

          {agentMessageListEl}

          {streamBusy && statusText ? <AgentProcessingStatusRow statusText={statusText} /> : null}
        </>
      )}
    </ChatCanvas>
  );
}
